import React from "react";
import { useTranslation } from "react-i18next";
import { GameRuleKey, GameRules } from "@uno/protocol";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import Switch from "component/Ui/Switch";
import Tooltip from "component/Tooltip";
import useMediaQuery from "hook/useMediaQuery";
import { HOUSE_RULES } from "utils/HouseRules";

export const DESKTOP_QUERY = "(min-width: 1024px), (orientation: landscape) and (min-width: 700px) and (max-height: 600px)";

type ListProps = {
    rules: GameRules;
    editable: boolean;
    onChange: (key: GameRuleKey, value: boolean) => void;
};

export const HouseRulesList = ({ rules, editable, onChange }: ListProps) => {
    const { t } = useTranslation();

    return (
        <ul className="flex flex-col gap-2 w-full">
            {HOUSE_RULES.map((rule) => (
                <li
                    key={rule.key}
                    className={`flex items-center gap-3 rounded-xl px-2.5 py-2 border transition-colors duration-200 ${
                        rules[rule.key] ? "bg-white/15 border-white/25" : "bg-white/5 border-white/10"
                    }`}
                >
                    <img src={rule.icon} alt="" width={36} height={36} className="w-9 h-9 shrink-0 drop-shadow" />
                    <div className="flex-1 min-w-0">
                        <p className="text-white text-[13px] font-bold leading-tight">{t(rule.name)}</p>
                        <p className="text-white/60 text-[11px] leading-snug mt-0.5">{t(rule.description)}</p>
                    </div>
                    <Switch
                        checked={rules[rule.key]}
                        disabled={!editable}
                        label={t(rule.name)}
                        onChange={(value) => onChange(rule.key, value)}
                    />
                </li>
            ))}
        </ul>
    );
};

type BarProps = {
    rules: GameRules;
    onOpen: () => void;
    variant?: "inline" | "column";
    showHint?: boolean;
};

export const HouseRulesBar = ({ rules, onOpen, variant = "inline", showHint = false }: BarProps) => {
    const { t } = useTranslation();
    const isColumn = variant === "column";

    return (
        <div className={`flex flex-col items-center ${isColumn ? "gap-3 w-full" : "gap-2"}`}>
            {isColumn && <div className="w-full h-px bg-white/10"></div>}
            <span
                className={isColumn
                    ? "font-extrabold text-sm viewport-landscape:text-[clamp(0.7rem,2.2vh,0.95rem)] uppercase tracking-[0.2em] text-white/90 drop-shadow-sm"
                    : "text-white/90 text-[10px] font-bold uppercase tracking-[0.15em]"}
            >
                {t("lobby.house_rules.button")}
            </span>
            <Tooltip text={t("lobby.house_rules.hint")} visible={showHint} placement={isColumn ? "bottom" : "top"} duration={6000} className="flex items-center gap-2.5">
                {HOUSE_RULES.map((rule) => {
                    const isOn = rules[rule.key];

                    return (
                        <ButtonHapticWrapper key={rule.key}>
                            <button
                                type="button"
                                onClick={onOpen}
                                title={t(rule.name)}
                                aria-label={`${t(rule.name)} : ${isOn ? t("lobby.house_rules.on") : t("lobby.house_rules.off")}`}
                                className={`relative w-10 h-10 rounded-full flex items-center justify-center cursor-pointer border transition-colors ${
                                    isOn ? "bg-white/20 border-white/40" : "bg-white/10 border-white/10 hover:bg-white/20"
                                }`}
                            >
                                <img
                                    src={rule.icon}
                                    alt=""
                                    width={24}
                                    height={24}
                                    className={`w-6 h-6 transition-[filter,opacity] ${isOn ? "" : "grayscale opacity-50"}`}
                                />
                                {isOn && (
                                    <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#2dbf25] border-2 border-[#1f5fc9]"></span>
                                )}
                            </button>
                        </ButtonHapticWrapper>
                    );
                })}
            </Tooltip>
        </div>
    );
};

type DialogProps = ListProps & {
    isOpen: boolean;
    onClose: () => void;
};

export const HouseRulesDialog = ({ isOpen, onClose, rules, editable, onChange }: DialogProps) => {
    const { t } = useTranslation();
    const isModal = useMediaQuery(DESKTOP_QUERY);
    const [isClosing, setIsClosing] = React.useState(false);

    const handleClose = React.useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 300);
    }, [onClose]);

    React.useEffect(() => {
        if(!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if(event.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, handleClose]);

    if(!isOpen) {
        return null;
    }

    const panelAnimation = isModal
        ? (isClosing ? "animate-backdrop-out" : "animate-backdrop-in")
        : (isClosing ? "animate-sheet-down" : "animate-sheet-up");

    return (
        <div className={`fixed inset-0 z-50 flex flex-col ${isModal ? "items-center justify-center p-4" : "justify-end"}`}>
            <div
                className={`absolute inset-0 bg-black/50 ${isClosing ? "animate-backdrop-out" : "animate-backdrop-in"}`}
                onClick={handleClose}
            ></div>

            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="house-rules-title"
                className={`relative px-5 ${isModal ? "w-full max-w-[26rem] rounded-2xl pt-5 pb-5 border border-white/15 shadow-2xl" : "rounded-t-2xl pt-3"} ${panelAnimation}`}
                style={{
                    background: "linear-gradient(180deg, #2570D9 0%, #1A5BC4 100%)",
                    ...(isModal ? {} : { borderTop: "1px solid rgba(255,255,255,0.15)", paddingBottom: "calc(var(--safe-bottom, 0px) + 2rem)" }),
                }}
            >
                {!isModal && <div className="w-8 h-0.5 rounded-full bg-white/20 mx-auto mb-5"></div>}
                <div className="flex items-center justify-between mb-3">
                    <h2 id="house-rules-title" className="text-white font-changa-one text-lg">{t("lobby.house_rules.title")}</h2>
                    {isModal && (
                        <button
                            type="button"
                            onClick={handleClose}
                            aria-label={t("home.cancel")}
                            className="w-8 h-8 -mr-1 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <HouseRulesList rules={rules} editable={editable} onChange={onChange} />
            </div>
        </div>
    );
};
